import type { BoardFetchResult } from '../api/boardsClient.js';
import type { PageDescriptor } from '../api/projectsClient.js';
import type { InitialProjectLoad } from './protectedProjectLoad.js';
import { parseNodeRouteUrl, type NodeKind, type NodeRoute } from './nodeRoute.js';

export type ProjectSurfaceDescriptor =
  | {
    kind: 'board';
    route: Extract<NodeRoute, { kind: 'board' }>;
    projectId: string;
    title: string;
    payload: BoardFetchResult;
  }
  | {
    kind: 'page';
    route: Extract<NodeRoute, { kind: 'page' }>;
    projectId: string;
    title: string;
    payload: PageDescriptor;
  };

export interface ProjectSurfaceRuntime {
  readonly ready: Promise<void>;
  prepareLeave(): Promise<boolean>;
  focusPrimary(): void;
  destroy(): void;
}

export type ProjectSurfaceErrorBucket =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'server'
  | 'network'
  | 'malformed';

export class ProjectSurfaceLoadError extends Error {
  readonly bucket: ProjectSurfaceErrorBucket;
  readonly status: number | undefined;

  constructor(bucket: ProjectSurfaceErrorBucket, status?: number) {
    super(`project surface descriptor failed: ${bucket}`);
    this.name = 'ProjectSurfaceLoadError';
    this.bucket = bucket;
    this.status = status;
  }
}

export type ProjectSurfacePhase = 'leave' | 'descriptor' | 'chrome' | 'runtime';
export type ProjectNavigationHistory = 'push' | 'pop' | 'replace';

export function projectSurfaceErrorBucket(error: unknown): ProjectSurfaceErrorBucket {
  return error instanceof ProjectSurfaceLoadError ? error.bucket : 'server';
}

export interface ProjectShellRouter {
  start(url: URL): Promise<void>;
  navigate(href: string, historyMode?: ProjectNavigationHistory): Promise<void>;
  suspendCurrent(): Promise<void>;
  destroy(): void;
}

interface ProjectShellRouterOptions {
  root: HTMLElement;
  basePath: string;
  initialLoad?: InitialProjectLoad;
  resolveDescriptor(route: NodeRoute, signal: AbortSignal): Promise<ProjectSurfaceDescriptor>;
  mountSurface(descriptor: ProjectSurfaceDescriptor): ProjectSurfaceRuntime;
  prepareChrome(descriptor: ProjectSurfaceDescriptor): void;
  prepareTreeLeave?: () => Promise<boolean>;
  setLoading(descriptor?: ProjectSurfaceDescriptor): void;
  setError(error: unknown, retry: () => void): void;
  onLeaveBlocked?: (error: unknown, retry: () => Promise<void>) => void;
  publishReady(descriptor: ProjectSurfaceDescriptor): void;
  onLogin(): void;
  track(event: string, properties: Record<string, unknown>): void;
  navigateDocument(href: string): void;
  readUrl?: () => URL;
  history?: Pick<History, 'pushState' | 'replaceState'>;
  eventTarget?: Pick<Window, 'addEventListener' | 'removeEventListener'> | EventTarget;
}

function canonicalHref(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

function phaseStatus(error: unknown): number | undefined {
  return error instanceof ProjectSurfaceLoadError ? error.status : undefined;
}

function sameRoute(left: NodeRoute, right: NodeRoute): boolean {
  return left.kind === right.kind
    && left.id === right.id
    && (left.kind !== 'page'
      || (right.kind === 'page' && left.fromBoardId === right.fromBoardId));
}

/** Own same-document board/page navigation without taking over other links. */
export function mountProjectShellRouter(options: ProjectShellRouterOptions): ProjectShellRouter {
  const readUrl = options.readUrl ?? (() => new URL(window.location.href));
  const history = options.history ?? window.history;
  const eventTarget = options.eventTarget ?? window;
  let generation = 0;
  let descriptorAbort: AbortController | null = null;
  let currentRuntime: ProjectSurfaceRuntime | null = null;
  let currentDescriptor: ProjectSurfaceDescriptor | null = null;
  let currentHref: string | null = null;
  let pendingHref: string | null = null;
  let pendingNavigation: Promise<void> | null = null;
  let initialLoad = options.initialLoad ?? null;
  let destroyed = false;
  if (initialLoad !== null) {
    void initialLoad.descriptor.catch(() => undefined);
    void initialLoad.rendererReady.catch(() => undefined);
  }

  const takeInitialLoad = (route: NodeRoute, href: string): InitialProjectLoad | null => {
    const candidate = initialLoad;
    initialLoad = null;
    if (candidate === null) return null;
    if (candidate.href === href && sameRoute(candidate.route, route)) return candidate;
    candidate.abort();
    return null;
  };

  const transition = (
    url: URL,
    historyMode: ProjectNavigationHistory | 'initial',
  ): Promise<void> => {
    const route = parseNodeRouteUrl(url, options.basePath);
    if (route === null || url.origin !== readUrl().origin) {
      initialLoad?.abort();
      initialLoad = null;
      if (historyMode !== 'initial') options.navigateDocument(url.href);
      return Promise.resolve();
    }
    const href = canonicalHref(url);
    const prefetched = takeInitialLoad(route, href);
    // Returning to the published URL must still supersede a different pending
    // transition; otherwise its late descriptor/runtime can repaint this URL.
    if (href === currentHref && pendingHref === null && currentRuntime !== null) return Promise.resolve();
    if (href === pendingHref && pendingNavigation !== null) return pendingNavigation;

    const targetGeneration = ++generation;
    descriptorAbort?.abort();
    const controller = new AbortController();
    if (prefetched !== null) {
      controller.signal.addEventListener('abort', () => prefetched.abort(), { once: true });
    }
    descriptorAbort = controller;
    pendingHref = href;
    const previousDescriptor = currentDescriptor;
    const fromKind: NodeKind | 'none' = previousDescriptor?.kind ?? 'none';
    const run = async (): Promise<void> => {
      let phase: ProjectSurfacePhase = 'leave';
      let candidate: ProjectSurfaceRuntime | null = null;
      try {
        const safeToLeave = await currentRuntime?.prepareLeave() ?? true;
        if (destroyed || targetGeneration !== generation) return;
        if (!safeToLeave) throw new Error('cannot leave project surface');
        if (!(await options.prepareTreeLeave?.() ?? true)) throw new Error('cannot leave project tree');
        if (destroyed || targetGeneration !== generation) return;
        if (historyMode === 'push' && href !== canonicalHref(readUrl())) history.pushState(null, '', href);
        else if (historyMode === 'replace') history.replaceState(null, '', href);
        options.setLoading(previousDescriptor ?? undefined);
        options.track('project_shell_navigation_started', {
          from_kind: fromKind,
          to_kind: route.kind,
          history: historyMode,
        });
        phase = 'descriptor';
        const descriptor = await (
          prefetched?.descriptor ?? options.resolveDescriptor(route, controller.signal)
        );
        if (destroyed || targetGeneration !== generation || controller.signal.aborted) return;
        phase = 'chrome';
        options.prepareChrome(descriptor);
        if (destroyed || targetGeneration !== generation || controller.signal.aborted) return;
        if (prefetched !== null && descriptor.kind === 'board') {
          await prefetched.rendererReady.catch(() => undefined);
          if (destroyed || targetGeneration !== generation || controller.signal.aborted) return;
        }
        currentRuntime?.destroy();
        currentRuntime = null;
        currentDescriptor = null;
        phase = 'runtime';
        candidate = options.mountSurface(descriptor);
        currentRuntime = candidate;
        await candidate.ready;
        if (destroyed || targetGeneration !== generation || currentRuntime !== candidate) {
          candidate.destroy();
          return;
        }
        currentDescriptor = descriptor;
        currentHref = href;
        if (historyMode !== 'initial') candidate.focusPrimary();
        options.publishReady(descriptor);
        options.track('project_shell_navigation_completed', {
          from_kind: fromKind,
          to_kind: descriptor.kind,
          project_changed: previousDescriptor?.projectId !== descriptor.projectId,
        });
      } catch (error) {
        if (destroyed || targetGeneration !== generation || controller.signal.aborted) {
          candidate?.destroy();
          return;
        }
        if (phase === 'leave') {
          if (historyMode === 'pop' && currentHref !== null
            && canonicalHref(readUrl()) !== currentHref) {
            history.replaceState(null, '', currentHref);
          }
          options.track('project_shell_navigation_blocked', {
            from_kind: fromKind,
            to_kind: route.kind,
            reason: 'unsaved_changes',
          });
          options.onLeaveBlocked?.(error, () => transition(url, historyMode === 'pop' ? 'push' : historyMode));
          return;
        }
        currentRuntime?.destroy();
        currentRuntime = null;
        currentDescriptor = null;
        currentHref = null;
        const bucket = projectSurfaceErrorBucket(error);
        options.track('project_shell_navigation_failed', {
          to_kind: route.kind,
          phase,
          status_bucket: phaseStatus(error) ?? bucket,
        });
        if (bucket === 'unauthorized') {
          options.onLogin();
          return;
        }
        options.setError(error, () => { void transition(url, 'replace'); });
      } finally {
        if (targetGeneration === generation) {
          pendingHref = null;
          pendingNavigation = null;
        }
      }
    };
    pendingNavigation = run();
    return pendingNavigation;
  };

  const navigate = (href: string, historyMode: ProjectNavigationHistory = 'push'): Promise<void> =>
    transition(new URL(href, readUrl()), historyMode);

  const handleClick = (event: Event): void => {
    if (!(event instanceof MouseEvent) || event.defaultPrevented || event.button !== 0
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const anchor = target.closest<HTMLAnchorElement>('a[href]');
    if (anchor === null || (anchor.target !== '' && anchor.target !== '_self')
      || anchor.hasAttribute('download')) return;
    const url = new URL(anchor.href, readUrl());
    if (url.origin !== readUrl().origin || parseNodeRouteUrl(url, options.basePath) === null) return;
    event.preventDefault();
    void transition(url, 'push');
  };

  const handlePopState = (): void => {
    void transition(readUrl(), 'pop');
  };
  options.root.addEventListener('click', handleClick);
  eventTarget.addEventListener('popstate', handlePopState);

  return {
    start: (url) => transition(url, 'initial'),
    navigate,
    async suspendCurrent(): Promise<void> {
      if (destroyed || currentRuntime === null) return;
      const runtime = currentRuntime;
      const targetGeneration = ++generation;
      descriptorAbort?.abort();
      descriptorAbort = null;
      initialLoad?.abort();
      initialLoad = null;
      pendingHref = null;
      pendingNavigation = null;
      const safeToSuspend = await runtime.prepareLeave();
      const safeTreeToSuspend = safeToSuspend ? await options.prepareTreeLeave?.() ?? true : false;
      if (!safeToSuspend || !safeTreeToSuspend || destroyed || targetGeneration !== generation || currentRuntime !== runtime) {
        throw new Error('cannot suspend project surface');
      }
      runtime.destroy();
      currentRuntime = null;
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      generation += 1;
      descriptorAbort?.abort();
      descriptorAbort = null;
      initialLoad?.abort();
      initialLoad = null;
      currentRuntime?.destroy();
      currentRuntime = null;
      currentDescriptor = null;
      options.root.removeEventListener('click', handleClick);
      eventTarget.removeEventListener('popstate', handlePopState);
    },
  };
}
